import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  useTableFilterSortData,
  useTableFilterSortSearchManager,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import useUniqueTableName from '../../../hooks/useUniqueTableName';
import useBaseTableProps from '../../../hooks/useBaseTableProps';
import useActionsFirstColumnOrder from '../../../hooks/useActionsFirstColumnOrder';
import {
  CERTIFICATION_TYPES,
  GROSS_RENT,
} from '../../ManageCertifications/constants';
import { HEADERS, PROPERTY_PATH_MAP } from './constants';
import messages from './messages';
import parentMessages from '../messages';
import { getHUDVoucherStatusBadgeVariant } from '../../../constants/badgeStatus';
import { capitalSnakeCaseToTitleCase } from '../../../utils/certificationTypeLabel';
import { getCertificationTypeDisplayValue } from '../../GeneralAffordable/getCertificationTypeDisplayValue';
import { HUD_PROGRAM_NAME } from '../../../constants/affordableProgramNames';
import { LOCKED_VOUCHER_SUBMISSION_STATUSES } from '../../../constants/hudVoucher';
import { getFirstMatchingReason } from '../utils';

const SUBMITTED_TRACS_STATUS = 'Submitted';

const numberFormatOptions = {
  minimumFractionDigits: 2,
};

const coerceBooleanish = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', 't', 'yes', 'y', '1'].includes(normalized)) return true;
    if (['false', 'f', 'no', 'n', '0'].includes(normalized)) return false;
    if (['y'].includes(normalized)) return true;
    if (['n'].includes(normalized)) return false;
  }
  return null;
};

const safeSort = (a, b) => {
  const strA = String(a || '');
  const strB = String(b || '');
  return strA.localeCompare(strB, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

const isRecertCertificationType = (certificationType) => {
  const normalized = (certificationType ?? '').toString().trim().toUpperCase();
  return normalized === CERTIFICATION_TYPES.RECERT;
};

const getRowSX = ({ certComplete }) => {
  const sx = {
    backgroundColor: 'background.paper',
    color: 'text.primary',
  };

  if (!certComplete) {
    sx.backgroundColor = 'error.lighter';
  }

  return sx;
};

function useBuildFilterOptions({ results }) {
  return useMemo(() => {
    const tracsStatusCounts = new Map();
    const floorPlanCounts = new Map(); // id -> { name, count }
    const certificationTypeCounts = new Map();
    const certCompleteCounts = { Y: 0, N: 0 };
    const correctionCounts = { Y: 0, N: 0 };

    (results ?? []).forEach((result) => {
      const tracsStatus = result?.tracsStatus ?? 'Not Submitted';
      tracsStatusCounts.set(
        tracsStatus,
        (tracsStatusCounts.get(tracsStatus) ?? 0) + 1,
      );

      const floorPlanName = result?.floorPlan ?? '';
      const floorPlanId = result?.floorPlanId ?? '';
      if (floorPlanName || floorPlanId) {
        const key = floorPlanId || floorPlanName;
        const existing = floorPlanCounts.get(key);
        floorPlanCounts.set(key, {
          name: floorPlanName || floorPlanId,
          count: (existing?.count ?? 0) + 1,
        });
      }

      if (result?.certificationType) {
        const ct = result.certificationType;
        certificationTypeCounts.set(
          ct,
          (certificationTypeCounts.get(ct) ?? 0) + 1,
        );
      }

      const certCompleteVal = result?.certComplete;
      if (certCompleteVal === 'Yes') certCompleteCounts.Y += 1;
      if (certCompleteVal === 'No') certCompleteCounts.N += 1;

      const correctionVal = result?.correction;
      if (correctionVal === 'Yes') correctionCounts.Y += 1;
      if (correctionVal === 'No') correctionCounts.N += 1;
    });

    const tracsStatus = Array.from(tracsStatusCounts.entries())
      .sort(([a], [b]) => safeSort(a, b))
      .map(([status, count]) => ({
        text: `${status} (${count})`,
        value: status,
      }));

    const floorPlanByName = new Map();
    Array.from(floorPlanCounts.values()).forEach(({ name, count }) => {
      floorPlanByName.set(name, (floorPlanByName.get(name) ?? 0) + count);
    });
    const floorPlan = Array.from(floorPlanByName.entries())
      .map(([name, count]) => ({
        text: `${name} (${count})`,
        value: name,
      }))
      .sort((a, b) => safeSort(a.text, b.text));

    const certificationType = Array.from(certificationTypeCounts.entries())
      .sort(([a], [b]) => safeSort(a, b))
      .map(([type, count]) => ({
        text: `${type} (${count})`,
        value: type,
      }));

    const certComplete = [
      certCompleteCounts.Y > 0 && {
        text: `Yes (${certCompleteCounts.Y})`,
        value: 'Yes',
      },
      certCompleteCounts.N > 0 && {
        text: `No (${certCompleteCounts.N})`,
        value: 'No',
      },
    ].filter(Boolean);

    const correction = [
      correctionCounts.Y > 0 && {
        text: `Yes (${correctionCounts.Y})`,
        value: 'Yes',
      },
      correctionCounts.N > 0 && {
        text: `No (${correctionCounts.N})`,
        value: 'No',
      },
    ].filter(Boolean);

    return {
      tracsStatus,
      floorPlan,
      certificationType,
      certComplete,
      correction,
    };
  }, [results]);
}

function useHeaders({ results }) {
  const filterOptions = useBuildFilterOptions({ results });

  return useMemo(
    () =>
      HEADERS.map((header) => ({
        ...header,
        filterOptions: header.filterOptions ?? filterOptions[header.id],
      })),
    [filterOptions],
  );
}

export const normalizeTenantEventForTable = (tenantEvent) => {
  const tracsSubmitted = coerceBooleanish(tenantEvent?.tracsSubmitted);
  const certIsComplete = coerceBooleanish(tenantEvent?.certComplete) === true;
  const tracsStatus = !certIsComplete
    ? 'Incomplete'
    : tracsSubmitted
    ? SUBMITTED_TRACS_STATUS
    : 'Not Submitted';

  const isRecert = isRecertCertificationType(tenantEvent?.certificationType);

  const getYesNoValue = (val) => {
    const boolVal = coerceBooleanish(val);
    if (boolVal === true) return 'Yes';
    return 'No';
  };

  const certComplete = getYesNoValue(tenantEvent?.certComplete);
  const correction = getYesNoValue(tenantEvent?.correction);

  const derivedHeadOfHousehold = (() => {
    const firstName = tenantEvent?.household?.mainCus?.firstName ?? '';
    const lastName = tenantEvent?.household?.mainCus?.lastName ?? '';
    const full = `${firstName} ${lastName}`.trim();
    return full || '';
  })();

  const floorPlan = tenantEvent?.unit?.floorPlan?.internalName ?? '';
  const floorPlanId = tenantEvent?.unit?.floorPlanId ?? '';

  return {
    id: tenantEvent?.id ?? '',
    affordableQualificationId: tenantEvent?.affordableQualificationId ?? '',
    tracsStatus,
    propertyId: tenantEvent?.propertyId ?? '',
    unitId: tenantEvent?.unit?.id ?? '',
    unitNumber: tenantEvent?.unit?.number ?? '',
    hudNumber: tenantEvent?.unit?.hudNumber ?? '',
    floorPlanId,
    floorPlan,
    headOfHousehold:
      tenantEvent?.headOfHousehold ?? derivedHeadOfHousehold ?? '',
    residentId: tenantEvent?.household?.mainCus?.rc?.residentId ?? '',
    effectiveDate: tenantEvent?.effectiveDate ?? null,
    certificationType: tenantEvent?.certificationType ?? '',
    certComplete,
    recertDueDate: isRecert ? tenantEvent?.recertDueDate ?? null : null,
    correction,
    assistanceAmount: tenantEvent?.subsidyRent ?? null,
  };
};

const NON_EDITABLE_VOUCHER_STATUSES = new Set(
  LOCKED_VOUCHER_SUBMISSION_STATUSES,
);

function useRows({
  tenantEvents,
  voucherSubmissionStatus,
  onMoveToNextVoucher,
  isMovingToNextVoucher,
  onDownloadCertification,
  isDownloadingCertification,
  canManage,
}) {
  const isMoveToNextVoucherDisabled =
    !canManage ||
    !onMoveToNextVoucher ||
    isMovingToNextVoucher ||
    (voucherSubmissionStatus &&
      NON_EDITABLE_VOUCHER_STATUSES.has(
        String(voucherSubmissionStatus).trim(),
      ));

  return useMemo(() => {
    return tenantEvents.map((row) => {
      const effectiveDate = row.effectiveDate;
      const tracsStatus = row.tracsStatus;
      const isRowMoveToNextVoucherDisabled =
        isMoveToNextVoucherDisabled || tracsStatus === SUBMITTED_TRACS_STATUS;
      const isCertComplete = row?.certComplete === 'Yes';
      const isDownloadCertificationDisabled =
        !canManage || !isCertComplete || isDownloadingCertification;
      const use59A = [
        CERTIFICATION_TYPES.TERMINATION,
        CERTIFICATION_TYPES.MOVE_OUT,
        CERTIFICATION_TYPES.UNIT_TRANSFER,
        GROSS_RENT,
      ].includes(row.certificationType);

      const rowSx = getRowSX({ certComplete: isCertComplete });
      const tracsStatusBadgeVariant =
        getHUDVoucherStatusBadgeVariant(tracsStatus);

      return {
        sx: rowSx,
        tracsStatus: {
          variant: 'badgeStatus',
          value: tracsStatusBadgeVariant,
          children: tracsStatus,
        },
        unitNumber: {
          variant: 'link',
          to: `/property/${row.propertyId}/unit/${row.unitId}`,
          value: row.unitNumber,
        },
        hudUnitNumber: {
          value: row.hudNumber,
        },
        floorPlan: {
          value: row.floorPlan,
        },
        headOfHousehold: {
          variant: 'link',
          to: `/property/${row.propertyId}/resident/${row.residentId}`,
          value: row.headOfHousehold,
        },
        residentRent: {
          variant: 'currency',
          value: row.residentRent,
          numberFormatOptions,
        },
        subsidyRent: {
          variant: 'currency',
          value: row.subsidyRent,
          numberFormatOptions,
        },
        utilityAllowance: {
          variant: 'currency',
          value: row.utilityAllowance,
          numberFormatOptions,
        },
        totalContractRent: {
          variant: 'currency',
          value: row.totalContractRent,
          numberFormatOptions,
        },
        requestedAmount: {
          variant: 'currency',
          value: row.requestedAmount,
          numberFormatOptions,
        },
        paidAmount: {
          variant: 'currency',
          value: row.paidAmount,
          numberFormatOptions,
        },
        effectiveDate: {
          variant: 'date',
          value: effectiveDate,
        },
        certificationType: {
          // HUD is hardcoded because tenant events only exist for HUD or HUD-layered
          // programs, and this reuses the Certification tab's shared label function so
          // the two screens can't drift apart.
          value: getCertificationTypeDisplayValue({
            programName: HUD_PROGRAM_NAME,
            certificationType: row.certificationType,
            certificationTypeValue: capitalSnakeCaseToTitleCase(
              row.certificationType,
            ),
          }),
        },
        certComplete: {
          value: row.certComplete,
        },
        recertDueDate: {
          variant: 'date',
          value: row.recertDueDate,
        },
        correction: {
          value: row.correction,
        },
        assistanceAmount: {
          variant: 'currency',
          numberFormatOptions,
          value: row.assistanceAmount,
        },
        actions: {
          variant: 'menu',
          name: 'actions',
          iconName: 'BoltIcon',
          color: 'primary',
          closeOnClick: true,
          options: [
            {
              value: 'download',
              text: (
                <FormattedMessage
                  {...(use59A
                    ? messages.download50059a
                    : messages.download50059)}
                />
              ),
              disabled: isDownloadCertificationDisabled,
              tooltip: getFirstMatchingReason([
                {
                  condition: !canManage,
                  message: (
                    <FormattedMessage {...parentMessages.noPermissionTooltip} />
                  ),
                },
                {
                  condition: !isCertComplete,
                  message: (
                    <FormattedMessage
                      {...messages.downloadCertificationDisabledTooltip}
                    />
                  ),
                },
              ]),
              tooltipProps: {
                placement: 'left-end',
                sx: { width: 'fit-content' },
              },
              onClick: () => {
                if (
                  !isDownloadCertificationDisabled &&
                  onDownloadCertification
                ) {
                  onDownloadCertification(
                    row.affordableQualificationId,
                    use59A,
                  );
                }
              },
            },
            {
              value: 'moveToNextVoucher',
              text: <FormattedMessage {...messages.moveToNextVoucher} />,
              disabled: isRowMoveToNextVoucherDisabled,
              tooltip: isRowMoveToNextVoucherDisabled
                ? getFirstMatchingReason([
                    {
                      condition: !canManage,
                      message: (
                        <FormattedMessage
                          {...parentMessages.noPermissionTooltip}
                        />
                      ),
                    },
                    {
                      condition: tracsStatus === SUBMITTED_TRACS_STATUS,
                      message: (
                        <FormattedMessage
                          {...messages.moveToNextVoucherDisabledTooltip}
                        />
                      ),
                    },
                  ])
                : undefined,
              tooltipProps: {
                placement: 'left-start',
                sx: { width: 'fit-content' },
              },
              onClick: () => {
                if (
                  !isRowMoveToNextVoucherDisabled &&
                  onMoveToNextVoucher &&
                  row.id
                ) {
                  onMoveToNextVoucher(row.id);
                }
              },
            },
          ],
        },
      };
    });
  }, [
    tenantEvents,
    onMoveToNextVoucher,
    isMoveToNextVoucherDisabled,
    onDownloadCertification,
    isDownloadingCertification,
    canManage,
  ]);
}

export function useNewTenantEventsTable({
  tenantEvents: tenantEventsProp = [],
  isLoading = false,
  voucherSubmissionStatus,
  onMoveToNextVoucher,
  isMovingToNextVoucher,
  onDownloadCertification,
  isDownloadingCertification,
  canManage,
}) {
  const name = useUniqueTableName('new-tenant-events-table');
  const baseTableProps = useBaseTableProps();

  const headers = useHeaders({ results: tenantEventsProp });

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers,
    initialOrderBy: 'effectiveDate',
    initialOrder: 'DESC',
  });

  const sortedAndFilteredResults = useTableFilterSortData({
    results: tenantEventsProp,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  const rows = useRows({
    tenantEvents: sortedAndFilteredResults,
    voucherSubmissionStatus,
    onMoveToNextVoucher,
    isMovingToNextVoucher,
    onDownloadCertification,
    isDownloadingCertification,
    canManage,
  });

  const { initialColumnsOrder, shouldForceOverwrite } =
    useActionsFirstColumnOrder({ name, headers });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
    initialColumnsOrder,
    forceOverwrite: shouldForceOverwrite,
  });

  return {
    baseTableProps,
    name,
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
    // Handle Sort/Filter/Search (server-driven)
    filterState,
    filterTypeState,
    searchState,
    dateState,
    handleSortChange,
    headers,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    // Indicators
    isLoading,
    order,
    orderBy,
    // Data
    rows,
    count: rows.length,
    totalCount: tenantEventsProp.length,
  };
}
